import React, { useCallback } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  Space,
  Radio,
  Select,
  Checkbox,
  Typography,
  Tooltip,
  AutoComplete,
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import BaseDatePicker from "../../../../components/BaseDatePicker/BaseDatePicker";
import { validateName } from "../../../../utils/validator";

const { TextArea } = Input;
const { Option } = Select;

// Constants
const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const OTHER_GENDER_OPTIONS = [
  { value: "unknown", label: "Unknown" },
  { value: "Choose Not to Disclose", label: "Choose Not to Disclose" },
];

const SEXUAL_ORIENTATION_OPTIONS = [
  { value: "Heterosexual", label: "Heterosexual (straight)" },
  { value: "Gay", label: "Gay" },
  { value: "Other", label: "Other" },
];

const OTHER_SEXUAL_ORIENTATION_OPTIONS = [
  { value: "Lesbian", label: "Lesbian" },
  { value: "Bisexual", label: "Bisexual" },
  { value: "Transgender", label: "Transgender" },
  { value: "Unknown", label: "Unknown" },
  { value: "Choose Not to Disclose", label: "Choose Not to Disclose" },
];

const MARITAL_STATUS_OPTIONS = [
  { value: "Single", label: "Single" },
  { value: "Married", label: "Married" },
  { value: "Other", label: "Other" },
];

const OTHER_MARITAL_STATUS_OPTIONS = [
  { value: "Widowed", label: "Widowed" },
  { value: "Divorced", label: "Divorced" },
  { value: "Unknown", label: "Unknown" },
  { value: "Choose Not to Disclose", label: "Choose Not to Disclose" },
];

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: "Employed", label: "Employed" },
  { value: "Unemployed", label: "Unemployed" },
  { value: "Student", label: "Student" },
  { value: "Other", label: "Other" },
];

const BasicInformationSection = ({
  conditionalFields,
  setConditionalFields,
  dateState,
  handleDateChange,
  disabledDate,
  firstNameOptions,
  handleSearchFirstName,
  lastNameOptions,
  handleSearchLastName,
}) => {
  const renderFormItem = useCallback((label, name, inputComponent, rules) => (
    <Col span={8} xs={24} md={12} lg={6}>
      <Form.Item label={label} name={name} rules={rules}>
        {inputComponent}
      </Form.Item>
    </Col>
  ), []);

  const handleGender = useCallback((e) => {
    const selectedGender = e.target.value;
    setConditionalFields(prev => ({ 
      ...prev, 
      gender: selectedGender === "Other" 
    }));
  }, [setConditionalFields]);

  const handleSexualOrientation = useCallback((e) => {
    const selectedSexualOrientation = e.target.value;
    setConditionalFields(prev => ({ 
      ...prev, 
      sexual_Orientation: selectedSexualOrientation === "Other" 
    }));
  }, [setConditionalFields]);

  const handleMaritalStatus = useCallback((e) => {
    const maritalStatus = e.target.value;
    setConditionalFields(prev => ({ 
      ...prev, 
      martialStatus: maritalStatus === "Other" 
    }));
  }, [setConditionalFields]);

  const handleMinorPatient = useCallback((e) => {
    const isMinor = e.target.checked;
    setConditionalFields(prev => ({ 
      ...prev, 
      isMinor 
    }));
  }, [setConditionalFields]);

  return (
    <div className="p-5 border-[1px] border-t-0 rounded-b-lg">
      <Row gutter={[16, 16]}>
        {renderFormItem(
          "First Name (Given Name)",
          "firstName",
          <AutoComplete
            showSearch
            options={firstNameOptions}
            allowClear={true}
            onSearch={handleSearchFirstName}
            placeholder="First Name"
            filterOption={false}
            notFoundContent={
              !firstNameOptions.length ? "No matches found" : null
            }
          />,
          [
            {
              required: true,
              message: "Please your first name",
            },
            {
              validator: validateName,
            },
          ]
        )}
        {renderFormItem(
          "Middle Name",
          "middleName",
          <Input placeholder="Middle Name" maxLength={30} />
        )}
        {renderFormItem(
          "Last Name (Family Name)",
          "lastName",
          <AutoComplete
            showSearch
            options={lastNameOptions}
            allowClear={true}
            onSearch={handleSearchLastName}
            placeholder="Last Name"
            filterOption={false}
            notFoundContent={
              !lastNameOptions.length ? "No matches found" : null
            }
          />,
          [
            {
              required: true,
              message: "Please enter your Last Name",
            },
            {
              validator: validateName,
            },
          ]
        )}
      </Row>

      <Row gutter={[16, 16]}>
        <Col>
          <Form.Item
            name={"dob"}
            label="Date of Birth"
            rules={[
              {
                required: true,
                message: "DOB is required",
              },
            ]}
          >
            <BaseDatePicker
              onChange={handleDateChange}
              disabledDate={disabledDate}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label="Age">
            {dateState.age !== null && (
              <Typography.Text>{`${dateState.age} years`}</Typography.Text>
            )}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={5} xs={20} md={10} lg={5}>
          <Form.Item
            name="gender"
            label="Gender"
            rules={[
              { required: true, message: "Please select a Gender" },
            ]}
          >
            <Radio.Group onChange={handleGender}>
              <Space direction="vertical">
                {GENDER_OPTIONS.map(option => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
          {conditionalFields.gender && (
            <Form.Item name="otherGender" className="mt-[-0.5rem]">
              <Select placeholder="Please Select">
                {OTHER_GENDER_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Col>

        <Col span={5} xs={20} md={10} lg={5}>
          <Form.Item
            name="sexualOrientation"
            label="Sexual Orientation"
          >
            <Radio.Group onChange={handleSexualOrientation}>
              <Space direction="vertical">
                {SEXUAL_ORIENTATION_OPTIONS.map(option => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
          {conditionalFields.sexual_Orientation && (
            <Form.Item
              name="otherSexualOrientation"
              className="mt-[-0.5rem]"
            >
              <Select placeholder="Please Select">
                {OTHER_SEXUAL_ORIENTATION_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Col>

        <Col span={5} xs={20} md={10} lg={5}>
          <Form.Item name="martialStatus" label="Marital Status">
            <Radio.Group onChange={handleMaritalStatus}>
              <Space direction="vertical">
                {MARITAL_STATUS_OPTIONS.map(option => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
          {conditionalFields.martialStatus && (
            <Form.Item
              name="otherMartialStatus"
              className="mt-[-0.5rem]"
            >
              <Select placeholder="Please Select">
                {OTHER_MARITAL_STATUS_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Col>

        <Col span={5} xs={20} md={10} lg={5}>
          <Form.Item
            name="employment_status"
            label="Employement Status"
          >
            <Radio.Group>
              <Space direction="vertical">
                {EMPLOYMENT_STATUS_OPTIONS.map(option => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16} className="mb-2">
        <Col span={24}>
          <Checkbox onChange={handleMinorPatient}>
            Legal guardian
            <Tooltip title="Please check the box if the patient has a legal guardian">
              <QuestionCircleOutlined className="ml-2" />
            </Tooltip>
          </Checkbox>
        </Col>
      </Row>

      {conditionalFields.isMinor && (
        <>
          <Row gutter={[16, 16]}>
            {renderFormItem(
              "First Name",
              "guardian_first_name",
              <Input placeholder="First Name" maxLength={30} />,
              [
                {
                  validator: validateName,
                },
              ]
            )}
            {renderFormItem(
              "Middle Name",
              "guardian_middle_name",
              <Input placeholder="Middle Name" maxLength={30} />,
              [
                {
                  validator: validateName,
                },
              ]
            )}
            {renderFormItem(
              "Last Name",
              "guardian_last_name",
              <Input placeholder="Last Name" maxLength={30} />,
              [
                {
                  validator: validateName,
                },
              ]
            )}
          </Row>
          <Row gutter={16}>
            <Col span={6} xs={24} md={12} lg={6}>
              <Form.Item
                name="guardianPhone"
                label="Guardian Phone"
                rules={[{ validator: require("../../../../utils/validator").phoneValidator }]}
              >
                <Input placeholder="Guardian Phone" maxLength="20" />
              </Form.Item>
            </Col>
            <Col span={6} xs={24} md={12} lg={6}>
              <Form.Item
                name="guardianEmail"
                label="Guardian Email"
                rules={[{ validator: require("../../../../utils/validator").emailValidator }]}
              >
                <Input placeholder="Guardian Email" maxLength={30} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      <Row gutter={16}>
        <Col span={12} sm={24} md={24} lg={12}>
          <Form.Item name="basicNotes" label="Notes">
            <TextArea showCount maxLength={300} placeholder="Notes" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default React.memo(BasicInformationSection);
